import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";
import { useSupportEmail } from "@/lib/config";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};

export default function ManageSubscription() {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const supportEmail = useSupportEmail();

  const load = async () => {
    const { data } = await api.get("/collectors/me");
    setProfile(data);
  };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/payments/cancel-subscription");
      toast.success("Subscription cancelled — it won't renew.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/payments/reactivate-subscription");
      toast.success("Subscription resumed.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!profile) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;

  const active = profile.subscription_active;
  const cancelled = profile.subscription_cancelled;
  const until = profile.subscription_until;

  return (
    <div className="container-x py-12 max-w-2xl" data-testid="manage-subscription-page">
      <Link to="/dashboard/collector" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B64] hover:text-[#CC5A3A] mb-6">
        <ArrowLeft className="w-4 h-4"/> Back to dashboard
      </Link>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Billing</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">Manage subscription</h1>
      <p className="text-sm text-[#5C6B64] mt-2">Your <span className="font-medium text-[#1E3F33]">$10.99/month</span> plan keeps your profile connected to customers. Cancel anytime.</p>

      <div className="card-soft mt-8" data-testid="subscription-status-card">
        {active && !cancelled && (
          <>
            <div className="flex items-center gap-2 text-[#2D6A4F]" data-testid="status-active">
              <ShieldCheck className="w-5 h-5"/>
              <span className="font-heading text-lg">Active</span>
            </div>
            <p className="text-sm text-[#5C6B64] mt-2">Your subscription is active and you're receiving customer leads. Active through <span className="font-medium text-[#1E3F33]">{fmtDate(until)}</span>.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="cancel-subscription-button" className="btn-secondary mt-5 text-[#B91C1C] border-[#B91C1C]/30">Cancel subscription</button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="cancel-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You'll keep access and continue receiving customers until <span className="font-medium">{fmtDate(until)}</span>. After that your profile stops being connected to customers and won't renew. You won't be charged again.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-dialog-dismiss">Keep subscription</AlertDialogCancel>
                  <AlertDialogAction data-testid="cancel-dialog-confirm" onClick={cancel} disabled={busy} className="bg-[#B91C1C] hover:bg-[#991818]">Yes, cancel</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}

        {active && cancelled && (
          <>
            <div className="flex items-center gap-2 text-[#B45309]" data-testid="status-cancelled">
              <AlertTriangle className="w-5 h-5"/>
              <span className="font-heading text-lg">Cancelled — won't renew</span>
            </div>
            <p className="text-sm text-[#5C6B64] mt-2">Your subscription is cancelled. You'll keep receiving customers until <span className="font-medium text-[#1E3F33]">{fmtDate(until)}</span>, after which your profile will no longer be connected to customers.</p>
            <button data-testid="reactivate-subscription-button" onClick={reactivate} disabled={busy} className="btn-primary mt-5 inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4"/> Resume subscription
            </button>
          </>
        )}

        {!active && (
          <>
            <div className="flex items-center gap-2 text-[#8A9A86]" data-testid="status-inactive">
              <AlertTriangle className="w-5 h-5"/>
              <span className="font-heading text-lg">No active subscription</span>
            </div>
            <p className="text-sm text-[#5C6B64] mt-2">Subscribe to start being connected to customers. Your profile won't appear in customer searches until your subscription is active.</p>
            <Link to="/subscribe" data-testid="subscribe-cta" className="btn-accent mt-5 inline-flex items-center gap-2">
              <CreditCard className="w-4 h-4"/> Subscribe — $10.99/mo
            </Link>
          </>
        )}
      </div>

      {(active || cancelled) && (
        <div className="card-soft mt-4" data-testid="billing-portal-card">
          <div className="flex items-center gap-2 text-[#1E3F33]">
            <CreditCard className="w-5 h-5 text-[#2D6A4F]" />
            <span className="font-heading text-lg">Payment method</span>
          </div>
          <p className="text-sm text-[#5C6B64] mt-2">Update your card, view invoices, or manage your plan securely in Stripe's billing portal.</p>
          <button data-testid="manage-billing-button" onClick={openPortal} disabled={busy} className="btn-secondary mt-5 inline-flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Manage billing in Stripe
          </button>
        </div>
      )}

      <p className="text-xs text-[#8A9A86] mt-4">Payments are processed securely by Stripe. For billing questions, contact {supportEmail}.</p>
    </div>
  );
}
